export default function Features() {
  const features = [
    {
      icon: "ri-lightbulb-line",
      color: "#12335f",
      gradientFrom: "#12335f",
      gradientTo: "#153577",
      title: "重要なのはDX戦略",
      description1: "DXが進まないケースの多くは、ツール選びから検討を始めてしまうことにあります。ツールはあくまで手段であり、目的や優先順位が定まらないまま導入しても、うまく活用できません。",
      description2: "デジポップブーストでは、ツール導入の前に、進め方そのものを戦略的に考えるを重視しています。",
      tableHeader: ["整理するポイント", "内容"],
      tableRows: [
        ["目的", "何を改善したいのか(問い合わせ、採用、業務効率など)"],
        ["優先順位", "今やることと、後回しにしてよいこと"],
        ["道筋", "最初の一歩と、その次にやること"],
      ],
      conclusion: "「どのツールを使うか」ではなく、",
      conclusionStrong: "何を実現するために、どこから着手するか",
      conclusionEnd: "を先に決めることで、迷わず進める状態をつくります。",
    },
    {
      icon: "ri-settings-3-line",
      color: "#037555",
      gradientFrom: "#037555",
      gradientTo: "#153577",
      title: "導入ではなく運用",
      description1: "DXは、導入しただけでは進みません。実際の業務の中で使われ続けてはじめて、意味を持ちます。",
      description2: "デジポップブーストでは、計画だけで終わらせず、現場で使われることを前提に進め方を整えます。",
      tableHeader: ["現場で止まりやすい理由", "進め方の考え方"],
      tableRows: [
        ["運用が複雑", "担当者が無理なく回せる形にする"],
        ["役割が不明確", "誰が何をするかを明確にする"],
        ["見直しが行われない", "改善のタイミングをあらかじめ作る"],
      ],
      conclusion: "「導入したかどうか」ではなく、",
      conclusionStrong: "日々の業務の中で自然に回っているか",
      conclusionEnd: "を重視します。",
    },
    {
      icon: "ri-book-open-line",
      color: "#ea5614",
      gradientFrom: "#ea5614",
      gradientTo: "#9a1e23",
      title: "ノウハウを残す支援",
      description1: "外部に任せきりにすると、一時的に負担は減ります。しかし、担当者の変更や支援終了をきっかけに、運用が止まってしまうケースも多いです。",
      description2: "デジポップブーストでは、社内にノウハウが残る伴走支援を大切にしています。",
      tableHeader: ["目指す状態", "内容"],
      tableRows: [
        ["分かる", "何をしているかが社内で共有されている"],
        ["できる", "社内で同じ進め方を再現できる"],
        ["戻れる", "困ったときに立て直せる"],
      ],
      conclusion: "「任せて終わり」ではなく、",
      conclusionStrong: "自社で回し続けられる状態を目指して進めるDX",
      conclusionEnd: "です。",
    },
  ];

  return (
    <section id="features" className="py-24 lg:py-32 bg-[#dce8f5] relative overflow-hidden">
      {/* 背景装飾 */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#c5d9f0]/50 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#b8d4f0]/40 rounded-full blur-3xl translate-x-1/4 translate-y-1/4"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* セクションヘッダー */}
        <div className="text-center mb-16">
          {/* セクションラベル */}
          <div className="inline-flex items-center justify-center px-5 py-2 bg-[#12335f] rounded-full shadow-sm mb-6">
            <span className="text-white font-semibold tracking-wide text-sm">Features</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-[#12335f] mb-6">
            なぜ、DX未経験でも前に進めるのか
          </h2>
          
          {/* 説明画像 */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="relative w-full h-64 rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/a52a13a88e2ccb39325c6dcb34729469/e1855b131be803282151964f060366b9.png"
                alt="DX推進のステップ"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          
          <p className="text-base lg:text-lg text-gray-700 leading-loose max-w-4xl mx-auto text-left">
            DXに初めて取り組む企業がつまずきやすいのは、専門知識やツールの有無ではありません。<br />
            進め方が見えないまま、検討だけが先行してしまうことです。
          </p>
          <p className="text-base lg:text-lg text-gray-700 leading-loose max-w-4xl mx-auto mt-6 text-left">
            デジポップブーストでは、DXに慣れていない企業でも迷わず動けるよう、最初から複雑な判断や高度な対応を求めません。<br />
            今の体制や業務の延長線上で取り組める形に落とし込みながら、一つずつ進めていく前提で設計しています。
          </p>
          <p className="text-base lg:text-lg text-gray-700 leading-loose max-w-4xl mx-auto mt-6 text-left">
            そのため、DXの経験がなくても、「考えて終わり」にならず、実際の行動につなげていくことができます。
          </p>
        </div>

        {/* 3カラムカード */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-16">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="bg-gray-50 rounded-2xl p-6 lg:p-8 shadow-sm border border-gray-100 relative flex flex-col h-full"
            >
              {/* 上部アクセントライン */}
              <div 
                className="absolute top-0 left-6 right-6 h-1 rounded-b-full"
                style={{ background: `linear-gradient(to right, ${feature.gradientFrom}, ${feature.gradientTo})` }}
              ></div>
              
              {/* アイコンとタイトル */}
              <div className="flex items-center mb-5 pt-4">
                <div 
                  className="w-11 h-11 flex items-center justify-center rounded-lg shadow-md mr-3"
                  style={{ backgroundColor: feature.color }}
                >
                  <i className={`${feature.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#12335f]">
                  {feature.title}
                </h3>
              </div>
              
              {/* 説明文 */}
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {feature.description1}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-5">
                {feature.description2}
              </p>
              
              {/* テーブル */}
              <div className="overflow-x-auto mb-5 flex-grow">
                <table className="w-full border-collapse text-sm rounded-lg overflow-hidden">
                  <thead>
                    <tr style={{ background: `linear-gradient(to right, ${feature.gradientFrom}, ${feature.gradientTo})` }}>
                      <th className="border border-gray-200 px-3 py-2.5 text-left text-xs font-bold text-white whitespace-nowrap">
                        {feature.tableHeader[0]}
                      </th>
                      <th className="border border-gray-200 px-3 py-2.5 text-left text-xs font-bold text-white whitespace-nowrap">
                        {feature.tableHeader[1]}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {feature.tableRows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                        <td className="border border-gray-200 px-3 py-2.5 text-xs text-gray-700 font-medium whitespace-nowrap">
                          {row[0]}
                        </td>
                        <td className="border border-gray-200 px-3 py-2.5 text-xs text-gray-700">
                          {row[1]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 結論 */}
              <div 
                className="rounded-lg p-4 border-l-4 mt-auto"
                style={{ 
                  background: `linear-gradient(to bottom right, ${feature.color}08, ${feature.gradientTo}08)`,
                  borderLeftColor: feature.color 
                }}
              >
                <p className="text-sm text-gray-700 leading-relaxed">
                  {feature.conclusion}<strong className="text-[#153577]">{feature.conclusionStrong}</strong>{feature.conclusionEnd}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
